/**
 * VideoSidebar - Integrated sidebar for video consultation
 * Contains Chat, Notes, and Akte tabs in a fixed position
 */

import { useState } from "react";
import { Room } from "livekit-client";
import { useTranslation } from "react-i18next";
import { Button } from "@jourfx/ui";
import { MessageSquare, FileText, User, X } from "lucide-react";
import { SidebarChatTab } from "./SidebarChatTab";
import { SidebarNotesTab } from "./SidebarNotesTab";
import { SidebarAkteTab } from "./SidebarAkteTab";

type TabType = "chat" | "notes" | "akte";

interface VideoSidebarProps {
  appointmentId: string;
  room: Room | null;
  isOpen: boolean;
  onClose: () => void;
  /** Demo mode skips Convex queries */
  isDemo?: boolean;
}

export function VideoSidebar({
  appointmentId,
  room,
  isOpen,
  onClose,
  isDemo = false,
}: VideoSidebarProps) {
  const { t } = useTranslation("video");
  const [activeTab, setActiveTab] = useState<TabType>("chat");

  if (!isOpen) return null;

  return (
    <aside className="w-96 bg-card border-l border-border flex flex-col h-full">
      {/* Header with tabs */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {t("sidebar.title")}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
            aria-label={t("sidebar.close")}
            data-testid="sidebar-close-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Tab buttons */}
        <div role="tablist" className="flex gap-1 bg-muted rounded-lg p-1">
          <button
            id="chat-tab"
            role="tab"
            aria-selected={activeTab === "chat"}
            aria-controls="chat-panel"
            onClick={() => setActiveTab("chat")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
              activeTab === "chat"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            {t("sidebar.tabs.chat")}
          </button>
          <button
            id="notes-tab"
            role="tab"
            aria-selected={activeTab === "notes"}
            aria-controls="notes-panel"
            onClick={() => setActiveTab("notes")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
              activeTab === "notes"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <FileText className="w-4 h-4" />
            {t("sidebar.tabs.notes")}
          </button>
          <button
            id="akte-tab"
            role="tab"
            aria-selected={activeTab === "akte"}
            aria-controls="akte-panel"
            onClick={() => setActiveTab("akte")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
              activeTab === "akte"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <User className="w-4 h-4" />
            {t("sidebar.tabs.file")}
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {activeTab === "chat" && (
          <div
            id="chat-panel"
            role="tabpanel"
            aria-labelledby="chat-tab"
            className="flex-1 flex flex-col min-h-0"
          >
            <SidebarChatTab appointmentId={appointmentId} />
          </div>
        )}
        {activeTab === "notes" && (
          <div
            id="notes-panel"
            role="tabpanel"
            aria-labelledby="notes-tab"
            className="flex-1 flex flex-col min-h-0"
          >
            <SidebarNotesTab appointmentId={appointmentId} isDemo={isDemo} />
          </div>
        )}
        {activeTab === "akte" && (
          <div
            id="akte-panel"
            role="tabpanel"
            aria-labelledby="akte-tab"
            className="flex-1 flex flex-col min-h-0"
          >
            <SidebarAkteTab appointmentId={appointmentId} isDemo={isDemo} room={room} />
          </div>
        )}
      </div>
    </aside>
  );
}
